export const LOCALIZED_PATHS = {
  en: {
    job: 'job',
    jobs: 'jobs',
    search: 'search',
    blog: 'resources',
    profile: 'profile',
    savedJobs: 'tools/saved-jobs',
    savedSearches: 'tools/saved-searches',
    appliedJobs: 'tools/applied-jobs',
    viewedJobs: 'tools/viewed-jobs',
    salaryComparison: 'salary-comparison',
    jobFlowSankey: 'tools/job-flow-sankey',
    business: 'business',
    privacy: 'privacy',
    terms: 'terms',
    forEmployers: 'for-employers',
    locations: 'locations',
    summerJobs: 'resources/summer-jobs',
    studentsAndGraduates: 'resources/students-and-graduates',
  },
  fi: {
    job: 'tyopaikka', // työpaikka without special characters
    jobs: 'tyopaikat', // työpaikat without special characters
    search: 'haku',
    blog: 'tyoelama',
    profile: 'profiili',
    savedJobs: 'tyokalut/tallennetut-tyopaikat',
    savedSearches: 'tyokalut/tallennetut-haut',
    appliedJobs: 'tyokalut/haetut-tyopaikat',
    viewedJobs: 'tyokalut/katsotut-tyopaikat',
    salaryComparison: 'palkkavertailu',
    jobFlowSankey: 'tyokalut/tyovirta-sankey',
    business: 'liiketoiminta',
    privacy: 'tietosuoja',
    terms: 'kayttoehdot',
    forEmployers: 'tyonantajille',
    locations: 'sijainnit',
    summerJobs: 'tyoelama/kesatyot',
    studentsAndGraduates: 'tyoelama/opiskelijat-ja-valmistuneet',
  },
  sv: {
    job: 'jobb',
    jobs: 'lediga-jobb',
    search: 'sok', // sök without special characters
    blog: 'arbetsliv',
    profile: 'profil',
    savedJobs: 'verktyg/sparade-jobb',
    savedSearches: 'verktyg/sparade-sokningar', // sökningar without special characters
    appliedJobs: 'verktyg/sokt-jobb',
    viewedJobs: 'verktyg/visade-jobb',
    salaryComparison: 'lonerjamforelse',
    jobFlowSankey: 'verktyg/jobbflode-sankey',
    business: 'foretag',
    privacy: 'integritet',
    terms: 'villkor',
    forEmployers: 'for-arbetsgivare',
    locations: 'platser',
    summerJobs: 'arbetsliv/sommarjobb',
    studentsAndGraduates: 'arbetsliv/studenter-och-nyexaminerade',
  },
} as const;

// Reverse mapping from English paths to LOCALIZED_PATHS keys
export const ENGLISH_PATH_TO_KEY: Record<string, keyof typeof LOCALIZED_PATHS.en> = {
  '/': 'search', // home/search page
  '/job': 'job',
  '/jobs': 'jobs', 
  '/search': 'search',
  '/blog': 'blog',
  '/resources': 'blog',
  '/profile': 'profile',
  '/tools/saved-jobs': 'savedJobs',
  '/tools/saved-searches': 'savedSearches',
  '/tools/applied-jobs': 'appliedJobs',
  '/tools/viewed-jobs': 'viewedJobs',
  '/salary-comparison': 'salaryComparison',
  '/tools/job-flow-sankey': 'jobFlowSankey',
  '/business': 'business',
  '/privacy': 'privacy',
  '/terms': 'terms',
  '/for-employers': 'forEmployers',
  '/locations': 'locations',
  '/resources/summer-jobs': 'summerJobs',
  '/resources/students-and-graduates': 'studentsAndGraduates',
} as const;

/**
 * Converts an English route path to a localized path for the given locale
 * @param englishPath - The English path (e.g., "/saved-jobs")
 * @param locale - Target locale ('en', 'fi', 'sv')
 * @returns Localized path (e.g., "/tallennetut-tyopaikat" for Finnish)
 */
export function localizeRoute(englishPath: string, locale: keyof typeof LOCALIZED_PATHS): string {
  // Handle root path specially
  if (englishPath === '/') {
    return '/';
  }
  
  // Handle resource hub and nested resource paths (alias of blog)
  if (englishPath === '/resources' || englishPath.startsWith('/resources/')) {
    const remainder = englishPath.replace(/^\/resources\/?/, '');
    const localizedBlog = LOCALIZED_PATHS[locale].blog;
    return remainder ? `/${localizedBlog}/${remainder}` : `/${localizedBlog}`;
  }
  // Backward-compat for old /blog nested paths
  if (englishPath.startsWith('/blog/')) {
    const remainder = englishPath.replace(/^\/blog\/?/, '');
    const localizedBlog = LOCALIZED_PATHS[locale].blog;
    return remainder ? `/${localizedBlog}/${remainder}` : `/${localizedBlog}`;
  }

  // Handle location-based paths (e.g., /jobs/location/Helsinki_Finland)
  if (englishPath.startsWith('/jobs/location/')) {
    const localizedJobs = LOCALIZED_PATHS[locale].jobs;
    const locationPart = englishPath.replace('/jobs/location/', '');
    // Use the correct location segment based on locale
    const locationSegment = locale === 'fi' ? 'alue' : locale === 'sv' ? 'omrade' : 'location';
    // Localize the location part (e.g., "Helsinki_Finland" -> "Helsinki_Suomi" for Finnish)
    const localizedLocationPart = localizeLocationPart(locationPart, locale);
    return `/${localizedJobs}/${locationSegment}/${localizedLocationPart}`;
  }

  // Handle company-based paths (e.g., /jobs/company/company-name)
  if (englishPath.startsWith('/jobs/company/')) {
    const localizedJobs = LOCALIZED_PATHS[locale].jobs;
    const companyPart = englishPath.replace('/jobs/company/', '');
    return `/${localizedJobs}/company/${companyPart}`;
  }

  // Handle other dynamic job paths (e.g., /jobs/role/developer)
  if (englishPath.startsWith('/jobs/')) {
    const localizedJobs = LOCALIZED_PATHS[locale].jobs;
    const jobPart = englishPath.replace('/jobs/', '');
    return `/${localizedJobs}/${jobPart}`;
  }

  // Remove leading slash for lookup
  const pathKey = ENGLISH_PATH_TO_KEY[englishPath];
  
  if (!pathKey) {
    // Return original path if not found in mapping (no warning for dynamic paths)
    return englishPath;
  }
  
  const localizedSegment = LOCALIZED_PATHS[locale][pathKey];
  return `/${localizedSegment}`;
}

// Build reverse mapping from localized paths to English paths
const LOCALIZED_PATH_TO_ENGLISH: Record<string, Record<string, string>> = {};

Object.keys(LOCALIZED_PATHS).forEach(locale => {
  if (locale === 'en') return; // Skip English as it's the base
  
  LOCALIZED_PATH_TO_ENGLISH[locale] = {};
  
  Object.entries(LOCALIZED_PATHS[locale as keyof typeof LOCALIZED_PATHS]).forEach(([key, localizedPath]) => {
    const englishPath = LOCALIZED_PATHS.en[key as keyof typeof LOCALIZED_PATHS.en];
    LOCALIZED_PATH_TO_ENGLISH[locale][localizedPath] = englishPath;
  });
});

/**
 * Converts a localized path back to its English equivalent
 * @param localizedPath - The localized path (e.g., "/tallennetut-tyopaikat")
 * @param locale - The locale of the path ('fi', 'sv')
 * @returns The English equivalent path (e.g., "/saved-jobs")
 */
export function delocalizeRoute(localizedPath: string, locale: keyof typeof LOCALIZED_PATHS): string {
  // If it's English locale, return as-is
  if (locale === 'en') {
    return localizedPath;
  }
  
  // Handle root path
  if (localizedPath === '/' || localizedPath === '') {
    return '/';
  }
  
  // Handle location-based paths (e.g., /tyopaikat/location/Helsinki_Finland -> /jobs/location/Helsinki_Finland)
  const localizedJobs = LOCALIZED_PATHS[locale].jobs;
  if (localizedPath.startsWith(`/${localizedJobs}/location/`)) {
    const locationPart = localizedPath.replace(`/${localizedJobs}/location/`, '');
    return `/jobs/location/${locationPart}`;
  }

  // Handle company-based paths (e.g., /tyopaikat/company/company-name -> /jobs/company/company-name)
  if (localizedPath.startsWith(`/${localizedJobs}/company/`)) {
    const companyPart = localizedPath.replace(`/${localizedJobs}/company/`, '');
    return `/jobs/company/${companyPart}`;
  }

  // Handle other dynamic job paths (e.g., /tyopaikat/role/developer -> /jobs/role/developer)
  if (localizedPath.startsWith(`/${localizedJobs}/`)) {
    const jobPart = localizedPath.replace(`/${localizedJobs}/`, '');
    return `/jobs/${jobPart}`;
  }
  
  // Remove leading slash for lookup
  const pathKey = localizedPath.startsWith('/') ? localizedPath.slice(1) : localizedPath;
  
  // Look up the English equivalent
  if (LOCALIZED_PATH_TO_ENGLISH[locale] && LOCALIZED_PATH_TO_ENGLISH[locale][pathKey]) {
    return `/${LOCALIZED_PATH_TO_ENGLISH[locale][pathKey]}`;
  }
  
  // Fallback: return original path
  return localizedPath;
}

export const BASE_URL = 'https://www.jobcrawls.com';
export const DEV_URL = 'http://localhost:3000';

/**
 * City name mappings across locales for URL generation
 */
const CITY_NAME_MAPPINGS: Record<string, { fi: string; sv: string; en: string }> = {
  'Helsinki': { fi: 'Helsinki', sv: 'Helsingfors', en: 'Helsinki' },
  'Helsingfors': { fi: 'Helsinki', sv: 'Helsingfors', en: 'Helsinki' },
  'Tampere': { fi: 'Tampere', sv: 'Tammerfors', en: 'Tampere' },
  'Tammerfors': { fi: 'Tampere', sv: 'Tammerfors', en: 'Tampere' },
  'Turku': { fi: 'Turku', sv: 'Åbo', en: 'Turku' },
  'Åbo': { fi: 'Turku', sv: 'Åbo', en: 'Turku' },
  'Abo': { fi: 'Turku', sv: 'Åbo', en: 'Turku' },
  'Oulu': { fi: 'Oulu', sv: 'Uleåborg', en: 'Oulu' },
  'Uleåborg': { fi: 'Oulu', sv: 'Uleåborg', en: 'Oulu' },
  'Uleaborg': { fi: 'Oulu', sv: 'Uleåborg', en: 'Oulu' },
  'Espoo': { fi: 'Espoo', sv: 'Esbo', en: 'Espoo' },
  'Esbo': { fi: 'Espoo', sv: 'Esbo', en: 'Espoo' },
  'Vantaa': { fi: 'Vantaa', sv: 'Vanda', en: 'Vantaa' },
  'Vanda': { fi: 'Vantaa', sv: 'Vanda', en: 'Vantaa' },
  'Vaasa': { fi: 'Vaasa', sv: 'Vasa', en: 'Vaasa' },
  'Vasa': { fi: 'Vaasa', sv: 'Vasa', en: 'Vaasa' },
  'Lahti': { fi: 'Lahti', sv: 'Lahtis', en: 'Lahti' },
  'Lahtis': { fi: 'Lahti', sv: 'Lahtis', en: 'Lahti' },
  'Kuopio': { fi: 'Kuopio', sv: 'Kuopio', en: 'Kuopio' },
  'Jyväskylä': { fi: 'Jyväskylä', sv: 'Jyväskylä', en: 'Jyväskylä' },
  'Jyvaskyla': { fi: 'Jyväskylä', sv: 'Jyväskylä', en: 'Jyväskylä' },
  'Pori': { fi: 'Pori', sv: 'Björneborg', en: 'Pori' },
  'Björneborg': { fi: 'Pori', sv: 'Björneborg', en: 'Pori' },
  'Bjorneborg': { fi: 'Pori', sv: 'Björneborg', en: 'Pori' },
  'Lappeenranta': { fi: 'Lappeenranta', sv: 'Villmanstrand', en: 'Lappeenranta' },
  'Kotka': { fi: 'Kotka', sv: 'Kotka', en: 'Kotka' },
  'Joensuu': { fi: 'Joensuu', sv: 'Joensuu', en: 'Joensuu' },
  'Rovaniemi': { fi: 'Rovaniemi', sv: 'Rovaniemi', en: 'Rovaniemi' },
  'Seinäjoki': { fi: 'Seinäjoki', sv: 'Seinäjoki', en: 'Seinäjoki' },
  'Mikkeli': { fi: 'Mikkeli', sv: 'S:t Michel', en: 'Mikkeli' },
  'Hyvinkää': { fi: 'Hyvinkää', sv: 'Hyvinge', en: 'Hyvinkää' },
  'Hämeenlinna': { fi: 'Hämeenlinna', sv: 'Tavastehus', en: 'Hämeenlinna' },
  'Remote': { fi: 'Etätyö', sv: 'Distans', en: 'Remote' },
  'Remote -': { fi: 'Etätyö -', sv: 'Distans -', en: 'Remote -' },
  'Etätyö': { fi: 'Etätyö', sv: 'Distans', en: 'Remote' },
  'Distans': { fi: 'Etätyö', sv: 'Distans', en: 'Remote' },
  'Etätyö -': { fi: 'Etätyö -', sv: 'Distans -', en: 'Remote -' },
  'Distans -': { fi: 'Etätyö -', sv: 'Distans -', en: 'Remote -' },
};

/**
 * Country name mappings for different locales
 */
const COUNTRY_NAME_MAPPINGS: Record<string, { fi: string; sv: string; en: string }> = {
  'Finland': { fi: 'Suomi', sv: 'Finland', en: 'Finland' },
  'Suomi': { fi: 'Suomi', sv: 'Finland', en: 'Finland' },
  'Europe': { fi: 'Eurooppa', sv: 'Europa', en: 'Europe' },
  'Europa': { fi: 'Eurooppa', sv: 'Europa', en: 'Europe' },
  'Eurooppa': { fi: 'Eurooppa', sv: 'Europa', en: 'Europe' },
  'Global': { fi: 'Globaali', sv: 'Globalt', en: 'Global' },
  'Globalt': { fi: 'Globaali', sv: 'Globalt', en: 'Global' },
  'Globaali': { fi: 'Globaali', sv: 'Globalt', en: 'Global' },
  'MENA': { fi: 'MENA', sv: 'MENA', en: 'MENA' },
};

/**
 * Localize location part (e.g., "Helsinki_Finland" -> "Helsinki_Suomi" for Finnish)
 */
function localizeLocationPart(locationPart: string, locale: keyof typeof LOCALIZED_PATHS): string {
  const parts = locationPart.split('_');
  if (parts.length === 0) return locationPart;
  
  // Handle special case where location has double underscore (e.g., "Remote_-_Global")
  // Remove empty strings from parts array (from split on "_-_")
  const cleanParts = parts.filter(p => p !== '');
  
  // Last part is country, everything before is city
  const country = cleanParts[cleanParts.length - 1];
  const city = cleanParts.slice(0, -1).join(' ');
  
  const mappedCity = CITY_NAME_MAPPINGS[city]?.[locale] || city;
  const mappedCountry = COUNTRY_NAME_MAPPINGS[country]?.[locale] || country;
  
  return `${mappedCity}_${mappedCountry}`;
}
